import { useEffect } from "react";
import PropTypes from "prop-types";
import { useAuthStore } from "../store/authStore";
import {
  getExportFileFormatOptions,
  getExportFilterOptions,
} from "./ToolsPage-options.js";

// Helper component for a single radio group;
// className sets its size in the surrounding flex container
const RadioGroup = ({
  title,
  name,
  options,
  selectedValue,
  onChange,
  className = "flex-1 h-full",
}) => (
  <div
    className={`${className} bg-gray-100 p-4 rounded-md border border-gray-200`}
  >
    <h3 className="text-lg font-medium mb-2 text-gray-800">{title}:</h3>
    <div className="flex flex-col space-y-2">
      {options.map((option) => (
        <label key={option.value} className="inline-flex items-center">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={selectedValue === option.value}
            onChange={onChange}
            className="form-radio h-4 w-4 text-cyan-700 border-gray-300 focus:ring-blue-500"
          />
          <span className="ml-2 text-gray-700">{option.label}</span>
        </label>
      ))}
    </div>
  </div>
);

RadioGroup.propTypes = {
  title: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

// Export Section Component (Admin, Member, User)
const ExportSection = ({
  dynamicHeight,
  exportFileFormat,
  setExportFileFormat,
  exportFilter,
  setExportFilter,
  exportVersionInput,
  handleExportVersionChange,
  handleExportVersionBlur,
  wrongInput,
  handleExport,
}) => {
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const isMember = useAuthStore((state) => state.isMember);

  // Get role-based options
  const exportFileFormatOptions = getExportFileFormatOptions(isAdmin, isMember);
  const exportFilterOptions = getExportFilterOptions(isAdmin, isMember);

  // Fallback to allowed defaults if current selection is not permitted
  useEffect(() => {
    const allowedFormats = exportFileFormatOptions.map((opt) => opt.value);
    if (!allowedFormats.includes(exportFileFormat)) {
      setExportFileFormat("json"); // Default to JSON
    }
  }, [exportFileFormat, exportFileFormatOptions, setExportFileFormat]);

  useEffect(() => {
    const allowedFilters = exportFilterOptions.map((opt) => opt.value);
    if (!allowedFilters.includes(exportFilter)) {
      setExportFilter("public"); // Default to Public Fields
    }
  }, [exportFilter, exportFilterOptions, setExportFilter]);

  // Hint beside the version field: shown only when the field is empty, or when
  // what has been typed so far is not valid semantic versioning.
  let versionHint = "";
  if (!exportVersionInput) {
    versionHint =
      exportFilter === "nextVersions"
        ? "Version needed."
        : "Empty for all versions.";
  } else if (wrongInput) {
    versionHint =
      "Please use semantic versioning:\n (major.minor.patch, e.g., 2.0.15).";
  }

  return (
    <div className="flex flex-col p-5 lg:min-h-[30rem] border border-gray-100 rounded-lg w-full max-w-xl bg-gradient-to-r from-cyan-700 to-cyan-800 text-white">
      <h2 className="text-2xl font-bold text-gray-100 mb-4">Export Data</h2>

      {/* RadioGroups Container */}
      <div className="flex gap-4 mb-6" style={{ height: `${dynamicHeight}px` }}>
        <RadioGroup
          title="File Format"
          name="exportFormat"
          options={exportFileFormatOptions}
          selectedValue={exportFileFormat}
          onChange={(e) => setExportFileFormat(e.target.value)}
        />

        <RadioGroup
          title="Filter"
          name="exportFilter"
          options={exportFilterOptions}
          selectedValue={exportFilter}
          onChange={(e) => setExportFilter(e.target.value)}
        />
      </div>

      {/* Version Input Section */}
      <div className="mb-6 text-gray-100">
        <label
          htmlFor="exportVersion"
          className="block text-gray-100 text-lg font-bold mb-2"
        >
          {exportFilter === "nextVersions"
            ? "Versions greater than:"
            : "Up to and incl. version:"}
        </label>
        <div className="flex flex-row items-center gap-4">
          <input
            type="text"
            id="exportVersion"
            name="exportVersion"
            value={exportVersionInput}
            placeholder="e.g., 2.18.3"
            onChange={handleExportVersionChange}
            onBlur={handleExportVersionBlur}
            className="w-[12rem] text-base flex items-center px-4 py-2 bg-cyan-700 text-gray-100 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:bg-gray-100 hover:text-gray-700 transition-colors duration-300 ease-in-out"
          />

          <div className="text-gray-100">{versionHint}</div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleExport}
        className={`w-full mt-auto font-semibold py-3 px-6 border border-gray-300 rounded-md shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-opacity-75
    ${
      (exportFilter === "nextVersions" &&
        (!exportVersionInput || wrongInput)) ||
      ((exportFilter === "public" || exportFilter === "all") &&
        exportVersionInput &&
        wrongInput)
        ? "bg-gray-400 text-white cursor-not-allowed"
        : "bg-cyan-600 text-white hover:bg-gray-200 hover:text-gray-800"
    }`}
        disabled={
          (exportFilter === "nextVersions" &&
            (!exportVersionInput || wrongInput)) ||
          ((exportFilter === "public" || exportFilter === "all") &&
            exportVersionInput &&
            wrongInput)
        }
      >
        Export Data
      </button>
    </div>
  );
};

ExportSection.propTypes = {
  dynamicHeight: PropTypes.number.isRequired,
  exportFileFormat: PropTypes.string.isRequired,
  setExportFileFormat: PropTypes.func.isRequired,
  exportFilter: PropTypes.string.isRequired,
  setExportFilter: PropTypes.func.isRequired,
  exportVersionInput: PropTypes.string.isRequired,
  handleExportVersionChange: PropTypes.func.isRequired,
  handleExportVersionBlur: PropTypes.func.isRequired,
  wrongInput: PropTypes.bool.isRequired,
  handleExport: PropTypes.func.isRequired,
};

export { ExportSection, RadioGroup };
